import React, { useState, useEffect, useCallback } from "react";
import Spinner from "../components/Spinner";
import MessageModal from "../components/MessageModal";
import fetchData from "../api/fetchData";
import { buildDemoCsv, buildTemplateCsv, couponsFromCsv, downloadCsv } from "../utils/couponCsv";
import CsvReviewModal from "../components/CsvReviewModal";

const UploadPage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [, setAllStatusesFinal] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState("");
  const [review, setReview] = useState(null);
  const [savingBatch, setSavingBatch] = useState(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    validateAndSetFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    validateAndSetFile(file);
  };

  const validateAndSetFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      alert("Please choose a .csv file.");
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  /**
   * Reads the CSV here rather than shipping it to storage for a worker.
   *
   * The old round trip meant a bad row surfaced as a missing coupon days later,
   * and the upload reported success either way. Parsing in the browser puts the
   * rows in front of the person who can fix them, before anything is stored.
   */
  const handleReviewFile = async () => {
    if (!selectedFile) {
      alert("Please select a file.");
      return;
    }
    setUploading(true);
    try {
      const text = await selectedFile.text();

      // Rows for a store that does not exist cannot be rendered anywhere, so
      // the store list is pulled once and used to flag them up front.
      let knownStores = null;
      const notes = [];
      try {
        const stores = await fetchData("/stores/all");
        knownStores = new Set((stores || []).map((s) => s.id));
      } catch (e) {
        console.warn("Could not load stores; rows will be checked on save instead", e);
        notes.push(
          "Could not load the store list, so stores are not checked here — the server still refuses coupons for a store that does not exist."
        );
      }

      const { coupons, headerProblems } = couponsFromCsv(text, knownStores);
      if (!coupons.length) {
        alert("No rows found in that file.");
        return;
      }
      setReview({
        fileName: selectedFile.name,
        coupons,
        headerProblems: [...headerProblems, ...notes],
        knownStores,
      });
    } catch (error) {
      console.error("Could not read the CSV:", error);
      alert(error?.message || "Could not read that file.");
    } finally {
      setUploading(false);
    }
  };

  const saveOneCoupon = async (coupon) => {
    await fetchData("/coupons/save", null, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(coupon),
    });
  };

  const saveAllCoupons = async (coupons) => {
    setSavingBatch(true);
    try {
      return await fetchData("/coupons/saveBatch", null, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(coupons),
      });
    } catch (error) {
      alert(error?.message || "Could not save the coupons.");
      return { results: [] };
    } finally {
      setSavingBatch(false);
    }
  };


  const fetchStatuses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchData("/status/all");

      // Sort by upload time so newest are at the top
      const sortedData = data.sort((a, b) => b.uploadDateTime - a.uploadDateTime);
      setStatuses(sortedData);

      const allFinal = sortedData.every(
        (status) => status.status === "Success" || status.status === "Failed"
      );
      setAllStatusesFinal(allFinal);
    } catch (error) {
      console.error("Error fetching statuses:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  const truncateMessage = (message) => {
    const words = message.split(" ");
    if (words.length > 3) {
      return words.slice(0, 3).join(" ") + "...";
    }
    return message;
  };

  const openModal = (message) => {
    setSelectedMessage(message);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedMessage("");
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 w-full items-center justify-start p-4 md:p-6 overflow-x-hidden">
      {/* Download Buttons */}
      <div className="w-full flex flex-col md:flex-row justify-end gap-2 mb-4 md:mb-0 md:absolute md:top-4 md:right-4 z-10">
        <button
          className="w-full md:w-auto px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 shadow-md text-sm md:text-base font-medium"
          title="A ready-to-edit CSV with the correct column names and two filled-in example rows"
          onClick={() => downloadCsv("coupon-demo.csv", buildDemoCsv())}
        >
          Download Demo CSV
        </button>
        <button
          className="w-full md:w-auto px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 shadow-md text-sm md:text-base font-medium"
          title="The column headers on their own, in the order the importer reads them"
          onClick={() => downloadCsv("coupon-template.csv", buildTemplateCsv())}
        >
          Download Template
        </button>
      </div>

      <main className="flex flex-col items-center p-4 md:p-8 bg-white shadow-lg rounded-lg w-full max-w-4xl mt-0 md:mt-12 overflow-hidden">
        <header className="mb-6 w-full text-center">
          <h1 className="text-xl md:text-3xl font-bold text-gray-800 break-words">
            Upload File to Server
          </h1>
        </header>

        <div
          className={`w-full mb-4 p-6 md:p-8 border-2 border-dashed rounded-lg ${dragging
            ? "border-red-600 bg-red-50"
            : "border-gray-300 bg-gray-50"
            } flex flex-col items-center justify-center text-center transition-colors duration-200 box-border`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="file-upload"
            className="hidden"
            accept=".csv"
            onChange={handleFileChange}
            disabled={uploading}
          />
          <div className="flex flex-col items-center justify-center w-full gap-4">
            {selectedFile ? (
              <div className="flex flex-col items-center gap-2">
                <span className="text-red-600 font-semibold break-all text-lg">
                  {selectedFile.name}
                </span>
                <button
                  type="button"
                  onClick={() => document.getElementById("file-upload").click()}
                  className="text-sm text-gray-500 hover:text-red-600 underline"
                >
                  Change File
                </button>
              </div>
            ) : (
              <>
                <p className="hidden md:block text-gray-600 text-lg font-medium">
                  Drag & Drop a CSV file here
                </p>
                <span className="hidden md:block text-gray-400 text-sm">
                  - OR -
                </span>
                <button
                  type="button"
                  onClick={() => document.getElementById("file-upload").click()}
                  className="px-6 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Browse Files
                </button>
                <p className="md:hidden text-gray-600 text-sm">
                  Tap button to select a CSV file
                </p>
              </>
            )}
          </div>
        </div>

        <button
          className={`w-full md:w-auto mt-2 px-8 py-3 text-white text-lg font-semibold rounded shadow-md transition-colors duration-200 ${!selectedFile || uploading
            ? "bg-gray-400 cursor-not-allowed"
            : "bg-red-600 hover:bg-red-700"
            }`}
          onClick={handleReviewFile}
          disabled={!selectedFile || uploading}
          style={{ minWidth: 'min(200px, 100%)' }}
        >
          {uploading ? "Reading…" : "Review coupons"}
        </button>

        <div className="w-full mt-10">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
            <h3 className="text-xl font-semibold text-gray-800">
              Status History
            </h3>
            <button
              className="w-full md:w-auto px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm md:text-base"
              onClick={fetchStatuses}
            >
              Refresh Table
            </button>
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto rounded-lg shadow border border-gray-200 w-full">
            <table className="min-w-full text-left">
              <thead className="bg-gray-100 text-gray-700 uppercase text-xs font-semibold">
                <tr>
                  <th className="p-3 border-b">File Name</th>
                  <th className="p-3 border-b">File Type</th>
                  <th className="p-3 border-b">Uploaded Time</th>
                  <th className="p-3 border-b">Last Updated By</th>
                  <th className="p-3 border-b">Last Updated Time</th>
                  <th className="p-3 border-b">Status</th>
                  <th className="p-3 border-b">Message</th>
                </tr>
              </thead>
              <tbody className="text-sm text-gray-700">
                {statuses.map((status, index) => (
                  <tr key={index} className="hover:bg-gray-50 border-b last:border-b-0 transition-colors">
                    <td className="p-3 max-w-xs truncate" title={status.fileName}>{status.fileName}</td>
                    <td className="p-3">{status.fileType}</td>
                    <td className="p-3">{new Date(status.uploadDateTime).toLocaleString()}</td>
                    <td className="p-3">{status.lastUpdatedBy}</td>
                    <td className="p-3">{new Date(status.lastUpdatedTime).toLocaleString()}</td>
                    <td className={`p-3 font-semibold ${status.status === "Success" ? "text-green-600" :
                      status.status === "Failed" ? "text-red-600" : "text-yellow-600"
                      }`}>
                      {status.status}
                    </td>
                    <td
                      className="p-3 truncate max-w-xs cursor-pointer text-red-600 hover:underline"
                      onClick={() => openModal(status.message)}
                      title={status.message}
                    >
                      {truncateMessage(status.message)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden flex flex-col gap-4 w-full">
            {statuses.map((status, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow w-full">
                <div className="flex justify-between items-start mb-2 gap-2">
                  <h4 className="font-bold text-gray-800 text-base break-all">{status.fileName}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-bold uppercase shrink-0 ${status.status === "Success" ? "bg-green-100 text-green-700" :
                    status.status === "Failed" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"
                    }`}>
                    {status.status}
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1 mb-3">
                  <p><span className="font-semibold">Type:</span> {status.fileType}</p>
                  <p><span className="font-semibold">Uploaded:</span> {new Date(status.uploadDateTime).toLocaleDateString()}</p>
                  <p><span className="font-semibold">Updated:</span> {new Date(status.lastUpdatedTime).toLocaleString()}</p>
                  <p><span className="font-semibold">By:</span> {status.lastUpdatedBy}</p>
                </div>
                {status.message && (
                  <div
                    className="text-sm bg-gray-50 p-2 rounded text-gray-700 cursor-pointer border border-gray-100 hover:bg-gray-100 break-words"
                    onClick={() => openModal(status.message)}
                  >
                    <span className="font-semibold block mb-1">Message:</span>
                    {truncateMessage(status.message)}
                  </div>
                )}
              </div>
            ))}
          </div>

        </div>
      </main>

      {review && (
        <CsvReviewModal
          fileName={review.fileName}
          rows={review.coupons}
          headerProblems={review.headerProblems}
          knownStores={review.knownStores}
          busy={savingBatch}
          onClose={() => {
            setReview(null);
            setSelectedFile(null);
          }}
          onSaveRow={saveOneCoupon}
          onSaveAll={saveAllCoupons}
        />
      )}

      <MessageModal
        isOpen={modalOpen}
        onClose={closeModal}
        message={selectedMessage}
      />
    </div>
  );
};

export default UploadPage;
